import {
  Anchor,
  Button,
  Checkbox,
  ConfirmDialog,
  Div,
  Flex,
  Input,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { List } from 'immutable'
import React from 'react'
import { usePersistedUserData, useSelector, useState } from 'src/hooks'
import { AffiliateFormInfo } from 'src/marketing/plans/components/AffiliateForm/AffiliateForm'
import { EditCard, PaymentTab } from 'src/payments/components'
import { creditCardBrand } from 'src/payments/components/CardLogo/CreditCardLogo'
import { FlexRow } from 'src/styled'

interface PaymentMethodProps {
  affiliateInfo: AffiliateFormInfo
  onFinishAccount: (info: AffiliateFormInfo) => void
  onBack: (info: AffiliateFormInfo) => void
  shouldHideAffiliateFields?: true
}

interface PaymentMethodState {
  govId1: string
  govId2: string
  accepted: boolean
  stripeToken?: stripe.Token
}

const formatGovID = (govID: string, type: 'EIN' | 'SSN'): string => {
  if (type === 'EIN' && govID.length > 2) {
    return List(govID).insert(2, '-').join('')
  } else if (type === 'SSN' && govID.length > 5) {
    return List(govID).insert(3, '-').insert(6, '-').join('')
  } else if (type === 'SSN' && govID.length > 3) {
    return List(govID).insert(3, '-').join('')
  } else {
    return govID
  }
}

const PaymentMethod: React.FC<PaymentMethodProps> = props => {
  const {
    affiliateInfo,
    onFinishAccount,
    onBack,
    shouldHideAffiliateFields,
  } = props

  const { account } = usePersistedUserData()

  const [details, setDetails] = useState<PaymentMethodState>({
    govId1: affiliateInfo?.govId1 ?? '',
    govId2: affiliateInfo?.govId2 ?? '',
    accepted: affiliateInfo?.accepted ?? false,
    stripeToken: affiliateInfo?.stripeToken ?? undefined,
  })
  const [errorMessage, setErrorMessage] = useState<string>()

  const card =
    details.stripeToken?.card ??
    (account?.stripeSource?.__typename === 'CreditCard'
      ? { last4: account.stripeSource.last4, brand: account.stripeSource.brand }
      : undefined)

  const isUS = affiliateInfo.user.country === 'United States'

  const hasCompany = affiliateInfo.user.company

  const formattedGovID1 = isUS
    ? formatGovID(details.govId1, affiliateInfo.user.company ? 'EIN' : 'SSN')
    : details.govId1

  const onBack2 = () => {
    onBack({
      ...affiliateInfo,
      govId1: details.govId1,
      govId2: details.govId2,
      accepted: details.accepted,
      stripeToken: details.stripeToken,
    })
  }

  const onSubmit = () => {
    if (isDisabled) {
      return
    }
    if (affiliateInfo.user.country !== 'United States' && !details.govId2) {
      setErrorMessage(
        'As an International Affiliate, a second form of government ID is required. Please add the Gov Id 2 and try again.',
      )
      return
    }
    onFinishAccount({
      ...affiliateInfo,
      govId1: formattedGovID1,
      govId2: details.govId2,
      accepted: details.accepted,
      stripeToken: details.stripeToken,
    })
    return
  }

  const handleChange = (
    name: keyof PaymentMethodState,
    value: string | boolean,
  ) => {
    setDetails({ ...details, [name]: value })
  }

  const submitCard = (token: stripe.Token) => {
    setDetails({ ...details, stripeToken: token })
  }

  const removeCard = () => {
    setDetails({ ...details, stripeToken: undefined })
  }

  const onDialogClose = () => {
    setErrorMessage(undefined)
  }

  const isMobile = useSelector(state => state.window.isMobile)

  const isDisabled = !details.govId1 || !details.accepted || !card

  const instructions = isUS
    ? hasCompany
      ? 'Enter the EIN associated with your business for tax purposes. If you do not have an EIN, please go back and remove the company name'
      : 'Enter your Social Security Number for tax purposes. If you have a company and would like to use your EIN, please go back and enter your company name.'
    : 'Provide two forms of identification This can include your driver’s license, passport, health card, etc'

  const placeholder1 = isUS
    ? hasCompany
      ? 'Employer Identification Number'
      : 'Social Security Number'
    : 'Government ID 1'

  return (
    <form>
      {!shouldHideAffiliateFields && (
        <>
          <Text type="body">{instructions}</Text>
          <Spacer space="x2" />
          <Flex width="100%" flexDirection="column">
            <Input
              onChange={govId1 =>
                handleChange(
                  'govId1',
                  isUS ? govId1.replaceAll(/[^0-9]/g, '').slice(0, 9) : govId1,
                )
              }
              type="text"
              name="govId1"
              value={formattedGovID1}
              placeholder={placeholder1}
              focusColor="primaryBrand"
              isFullWidth={true}
            />
            <Spacer space={'x1'} />
            {!isUS && (
              <Input
                onChange={govId2 => handleChange('govId2', govId2)}
                type="text"
                name="govId2"
                value={details.govId2}
                placeholder={'Government ID 2'}
                focusColor="primaryBrand"
                isFullWidth={true}
              />
            )}
            <Spacer space={'x3'} />
            <FlexRow>
              <div>
                <Checkbox
                  onChange={accepted => handleChange('accepted', accepted)}
                  isChecked={details.accepted}
                  type="primary"
                />
              </div>
              <Text type="caption">
                I agree to the Affiliate Agreement, Compensation Plan, and Terms
                and Conditions. I also authorize Greener Still to charge me
                $14/month to maintain my Affiliate websites, apps, etc.
                necessary to facilitate my business.
              </Text>
            </FlexRow>
            <Spacer space={'x2'} />
            <Flex justifyContent="space-around">
              <Anchor
                href="https://www.sendoutcards.com/resources/documents/391/Affiliate_Agreement_Documents_July_1_2020.pdf"
                title="Agreement"
                color="anchorBlue"
                size="xSmall"
              />
              <Anchor
                href="https://www.sendoutcards.com/resources/documents/275/COMPENSATION_PLAN_DETAILS_June_2020.pdf"
                title="Comp Plan Details"
                color="anchorBlue"
                size="xSmall"
              />
            </Flex>
            <Spacer space={'x4'} />
          </Flex>
        </>
      )}
      <Text type="subtitle">Payment Method</Text>
      <Spacer space={'x2'} />
      {card ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <PaymentTab
            paymentType={'card'}
            last4={card.last4}
            brand={creditCardBrand(card.brand)}
          />
          <Button
            fullWidth={false}
            type="secondary"
            title="Remove Card"
            onClick={() => removeCard()}
          />
        </div>
      ) : (
        <EditCard
          onSuccess={submitCard}
          onError={setErrorMessage}
          isSubmitButtonVisible={true}
          isAddingCard={false}
        />
      )}
      <ConfirmDialog
        title={'Card Error'}
        description={
          errorMessage ?? 'There was an error with your card. Please try again.'
        }
        isOpen={!!errorMessage}
        accept={{ title: 'Ok', onClick: onDialogClose }}
      />
      <Flex width="100%" flexDirection="column">
        {!shouldHideAffiliateFields && (
          <>
            <Spacer space={isMobile ? 'x1' : 'x4'} />
            <Div width="100%" id="finish_affiliate_account_btn">
              <Button
                fullWidth={true}
                type={'primary'}
                onClick={() => onSubmit()}
                title="Finish Joining"
                disabled={isDisabled}
              />
            </Div>
          </>
        )}
        <Spacer space={isMobile ? 'x1' : 'x4'} />
        <Div width="100%" id="back_btn">
          <Button
            fullWidth={true}
            type={'secondary'}
            onClick={() => onBack2()}
            title={'Back'}
          />
        </Div>
      </Flex>
      <Spacer space="x2" />
    </form>
  )
}

export default PaymentMethod
